import React from 'react'
import { FormProvider } from 'react-hook-form'
import { INLINE_MESSAGE_VARIANTS } from 'transfix-ui/components/InlineMessage'
import { ModalBase, IModalBaseProps, MODAL_TYPES } from 'transfix-ui/components/Modals'
import { IShipperApplicationContact } from 'types/graphqlTypes'
import { useUpsertShipperContacts } from '../../../../ShipperContacts/ShipperContactsModal/utils/useUpsertShipperContacts'
import { useTrailerManagementContext as useShipperContext } from '../../../../ShipperTrailers/TrailerManagementContext'
import { DEFAULT_EDI_CONTACT_VALUES, EdiContactsForm } from '../forms/EdiContactsForm'
import { useEdiContactsForm } from '../hooks/useEdiContactsForm'
import { IEdiContactRoles } from '../schemas/EdiContactsSchema'

interface IEdiContactsProps extends Omit<IModalBaseProps, 'modalType'> {
  contacts: IShipperApplicationContact[] | undefined
}

const ediContactRoles = Object.values(IEdiContactRoles)

export const EdiContactsModal = ({
  contacts,
  onClose,
  open = false,
  ...props
}: IEdiContactsProps) => {
  const { shipperId } = useShipperContext()

  const defaultContacts = contacts?.length
    ? contacts.filter(({ role }) => ediContactRoles.includes(role))
    : [{ ...DEFAULT_EDI_CONTACT_VALUES }]

  const methods = useEdiContactsForm({
    defaultValues: {
      contacts: defaultContacts,
    },
  })

  const { handleSubmit, setError: setFormError } = methods || {}

  const { apiErrors, isUpserting, upsertContacts } = useUpsertShipperContacts({
    shipperApplicationId: shipperId,
    setFormError,
  })

  const actions: IModalBaseProps['actions'] = [
    {
      label: 'Cancel',
      action: () => {
        onClose()
      },
      type: 'dismiss',
      dataTest: 'cancel-button',
      disabled: isUpserting,
    },
    {
      label: 'Save',
      action: () => {
        const nonEdiContacts = contacts?.filter(({ role }) => !ediContactRoles.includes(role))
        // removing __typename property from ediContacts
        // eslint-disable-next-line @typescript-eslint/no-unused-vars, unused-imports/no-unused-vars
        const cleanedNonEdiContacts = nonEdiContacts?.map(({ __typename, ...rest }) => rest) || []
        const currentContacts = methods.getValues()?.contacts || []
        const updatedContacts = [...currentContacts, ...cleanedNonEdiContacts]

        handleSubmit(upsertContacts(onClose, updatedContacts))()
      },
      type: 'confirm',
      dataTest: 'confirm-button',
      disabled: isUpserting,
    },
  ]

  const inlineMessageProps = apiErrors.length
    ? {
        message: apiErrors,
        type: INLINE_MESSAGE_VARIANTS.ALERT,
      }
    : undefined

  return (
    <ModalBase
      dataTest='EdiContactsModal'
      title='EDI Contacts'
      modalType={MODAL_TYPES.form}
      size={8}
      open={open}
      actions={actions}
      onClose={onClose}
      inlineMessageProps={inlineMessageProps}
      {...props}>
      <FormProvider {...methods}>
        <EdiContactsForm />
      </FormProvider>
    </ModalBase>
  )
}
